import { FilterAlt } from '@mui/icons-material'
import { Box, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import useDocumentCountPerWorkflowState from '@/hooks/useDocumentCountPerWorkflowState'
import useGetThemeColor from '@/hooks/useGetThemeColor'
import { ThemeColor } from '@/theme/usePixydocsTheme'
import { WorkflowState } from '@/types/workflows'
import { getWorkflowStateGroupsBySortOrder } from '@/utils/workflow-states'
import { useDocumentFiltersContext } from '@/components/document-filters/DocumentFiltersProvider'
import LeftBorderedBox from '@/components/validation/LeftBorderedBox'

type StateFilterProps = {
  workflowStates: WorkflowState[]
  setWorkflowState: (stateId: string) => void
  selectedWorkflowState?: WorkflowState
  showCount?: boolean
  miniMode?: boolean
}

type StateHeadingProps = {
  isApplyingFilters: boolean
}

type StateItemProps = {
  label: string
  onClick: () => void
  miniMode: boolean
  color: ThemeColor
  isApplyingFilters?: boolean
  isSelected?: boolean
  showMiniBorder?: boolean
  idleCount?: number | string
  showCount?: boolean
}

function StateItem({
  label,
  onClick,
  miniMode,
  color,
  isApplyingFilters,
  isSelected,
  showMiniBorder,
  idleCount,
  showCount,
}: StateItemProps) {
  const theme = useTheme()
  return (
    <Tooltip
      title={label}
      placement="right"
      enterDelay={miniMode ? 0 : 2_000}
      disableHoverListener={!miniMode && label.length <= 30}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        role="button"
        onClick={onClick}
        spacing={1}
        sx={{
          minHeight: 28,
          position: 'relative',
          cursor: 'pointer',
          borderRadius: 1,
          px: !miniMode ? 0.5 : 0,
          pl: 0,
          background: isSelected ? theme.palette.action.selected : undefined,
          '&:hover': {
            background: theme.palette.action.hover,
          },
        }}
      >
        <LeftBorderedBox
          color={color}
          showMiniBorder={showMiniBorder}
          sx={{ minWidth: 0 }}
        >
          <Box sx={{ pl: 1.5 }}>
            {!miniMode && (
              <Typography variant="body2" component="div" noWrap>
                {label}
              </Typography>
            )}
          </Box>
        </LeftBorderedBox>

        {!miniMode && showCount && (
          <Typography
            component="span"
            color="textSecondary"
            variant="body2"
            sx={{
              fontStyle: isApplyingFilters ? 'italic' : 'normal',
            }}
          >
            {idleCount}
          </Typography>
        )}
      </Stack>
    </Tooltip>
  )
}

function StatesHeading({ isApplyingFilters }: StateHeadingProps) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ height: 28 }}>
      <Typography variant="body1">Workflow States</Typography>
      {isApplyingFilters && (
        <Tooltip title="Filters Applied" enterDelay={500}>
          <FilterAlt fontSize="small" />
        </Tooltip>
      )}
    </Stack>
  )
}

export default function StateFilter({
  workflowStates,
  selectedWorkflowState,
  setWorkflowState,
  showCount,
  miniMode = false,
}: StateFilterProps) {
  const theme = useTheme()
  const getThemeColor = useGetThemeColor()
  const { isApplyingFilters } = useDocumentFiltersContext()

  const {
    idleCountByStateId,
    inFlightCountByStateId,
    filteredIdleCountByStateId,
  } = useDocumentCountPerWorkflowState()

  const displayableWorkflowStates = workflowStates.filter(
    (state) => state.code !== 'processing',
  )
  const stateGroupsBySortOrder = getWorkflowStateGroupsBySortOrder(
    displayableWorkflowStates,
  )

  return (
    <Stack direction="column" spacing={1}>
      {!miniMode && (
        <>
          <StatesHeading isApplyingFilters={isApplyingFilters} />
          {workflowStates.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              <i>No Workflow States</i>
            </Typography>
          )}
        </>
      )}

      <StateItem
        label="All States"
        isSelected={!selectedWorkflowState}
        miniMode={miniMode}
        onClick={() => setWorkflowState('')}
        color={theme.palette.mode === 'dark' ? 'white' : 'black'}
      />

      {stateGroupsBySortOrder.map((states) => {
        if (states.length === 0) return
        const workflowStateType = states[0].workflow_state_type
        return (
          <Stack key={states[0].id}>
            {!miniMode && workflowStateType && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block' }}
              >
                {workflowStateType.name}
              </Typography>
            )}
            {states.map((state) => {
              const idleCount = isApplyingFilters
                ? filteredIdleCountByStateId[state.id] || '-'
                : idleCountByStateId[state.id] || '-'
              const isSelected = selectedWorkflowState?.id === state.id
              const color = getThemeColor(state.color)
              return (
                <StateItem
                  key={state.id}
                  label={state.name}
                  isApplyingFilters={isApplyingFilters}
                  isSelected={isSelected}
                  miniMode={miniMode}
                  onClick={() => setWorkflowState(state.id)}
                  color={color}
                  showMiniBorder={!!inFlightCountByStateId[state.id]}
                  idleCount={idleCount}
                  showCount={showCount}
                />
              )
            })}
          </Stack>
        )
      })}
    </Stack>
  )
}
